/**
 * Rekap upah borongan per group — pertanyaan yang jadi alasan modulnya ada.
 *
 * ⛔ Selama dua puluh hari, ETAMSIOS punya mesin upah borongan yang matang dan
 * NOL cara menjawab *"group Jahit-2 dapat berapa bulan ini?"*. Layar ini yang
 * menutupnya.
 *
 * ---------------------------------------------------------------------------
 * ⛔ TIGA HAL YANG DITAMPILKAN SENGAJA
 * ---------------------------------------------------------------------------
 *
 *  1. **`tanpa_group`** — rincian upah yang serah terimanya nol menyebut group.
 *     Menyembunyikannya membuat total di layar nol sama dengan jumlah baris
 *     yang ada, dan selisih upah yang nol bisa dijelaskan adalah selisih yang
 *     akan ditanyakan orang.
 *
 *  2. **Tarif per jenis bisa LEBIH DARI SATU.** Itu bukan cacat melainkan inti
 *     K13/K77: tarif dibekukan per FA. Bulan yang memuat FA lama dan FA baru
 *     sekaligus memang punya dua tarif untuk jenis yang sama, dan menampilkan
 *     satu angka akan berbohong.
 *
 *  3. **Keadaan kosong yang menyebut SEBABNYA.** "Nol ada data" terbaca sebagai
 *     kerusakan; "belum ada serah terima bulan ini" terbaca sebagai keadaan.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import SetelanPeriodeUpah from "@/components/etams/SetelanPeriodeUpah";
import TandaDibayarUpah from "@/components/etams/TandaDibayarUpah";
import Hint from "@/components/etams/Hint";

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
// Selisih koreksi bisa minus: tanda ditulis di depan "Rp".
const rupiahTanda = (n) => (Number(n) < 0 ? "−" : "+") + rupiah(Math.abs(Number(n || 0)));
const tgl = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });

// K97 (23 Sep 2026): rekap per PERIODE region (frekuensi + cut-off ditentukan Kepala Cabang),
// bukan bulan kalender. Periode yang sudah terbentuk menyimpan tanggalnya sendiri.
export default function BoronganRekapUpah() {
  const { roleCode } = useAuth();
  const lintas = roleCode === "OWNER" || roleCode === "FINANCE";
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [info, setInfo] = useState(null);          // {setelan, periode[], boleh_ubah}
  const [periodeId, setPeriodeId] = useState("");
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [buka, setBuka] = useState(null);

  useEffect(() => {
    if (!lintas) return;
    api.get("/regions").then((r) => {
      const d = r.data || [];
      setRegions(d);
      if (d.length) setRegionId((x) => x || d[0].id);
    }).catch((e) => setGalat(apiErrorMessage(e)));
  }, [lintas]);

  const muatPeriode = useCallback(async () => {
    if (lintas && !regionId) return;
    try {
      const { data: d } = await api.get("/borongan/periode", { params: lintas ? { region_id: regionId } : {} });
      setInfo(d);
      setPeriodeId((x) => (d.periode.some((p) => p.id === x) ? x : (d.periode[0]?.id || "")));
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setInfo(null);
    }
  }, [lintas, regionId]);
  useEffect(() => { muatPeriode(); }, [muatPeriode]);

  const muatData = useCallback(async () => {
    if (!periodeId) { setMuat(false); return; }
    setMuat(true);
    setGalat("");
    try {
      const { data: d } = await api.get("/borongan/rekap-upah", {
        params: { periode_id: periodeId },
      });
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setData(null);
    } finally {
      setMuat(false);
    }
  }, [periodeId]);

  useEffect(() => { muatData(); }, [muatData]);

  const periode = (info?.periode || []).find((p) => p.id === periodeId);
  const bulan = periode ? `${tgl(periode.mulai)} – ${tgl(periode.selesai)}` : "periode ini";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Rekap Upah Borongan{" "}
            <Hint>Dijumlahkan dari rincian serah terima Jahit. Angkanya dihitung sistem, bukan di layar ini.</Hint>
          </h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {lintas && (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">Wilayah</span>
              <select className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                      value={regionId} onChange={(e) => setRegionId(e.target.value)} data-testid="rekap-region">
                {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">Periode</span>
            <select className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                    value={periodeId} onChange={(e) => setPeriodeId(e.target.value)} data-testid="rekap-periode">
              {(info?.periode || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {tgl(p.mulai)} – {tgl(p.selesai)}{p.dibayar ? " · sudah dibayar" : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {info && (
        <SetelanPeriodeUpah regionId={info.region_id} setelan={info.setelan}
                            bolehUbah={info.boleh_ubah} onTersimpan={muatPeriode} />
      )}

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Menghitung…</p>}

      {/* K212 / BUSINESS-82: status "sudah dibayar" — kuncinya hanya dikirim server untuk Owner/Finance/KC cabangnya. */}
      {!muat && data && "dibayar" in data && (
        <TandaDibayarUpah periode={data.periode} dibayar={data.dibayar}
                          bolehTandai={Boolean(data.boleh_tandai_dibayar)}
                          onBerubah={() => { muatPeriode(); muatData(); }} />
      )}

      {!muat && data && data.group.length === 0 && data.total_idr === 0 && (
        /* ⛔ Butir 3 — menyebut SEBABNYA, bukan cuma "nol ada data". */
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Belum ada serah terima Jahit tercatat pada {bulan}, jadi belum ada
          upah yang bisa dihitung.
        </div>
      )}

      {!muat && data && (data.group.length > 0 || data.total_idr !== 0) && (
        <>
          <div className="rounded-xl border border-border bg-card p-4">
            <span className="block text-xs text-muted-foreground">Total upah {bulan}</span>
            <span className="tabular-nums text-2xl font-semibold text-foreground">
              {rupiah(data.total_idr)}
            </span>
          </div>

          {data.group.map((g) => (
            <div key={g.sewing_group_id} className="rounded-xl border border-border bg-card p-4">
              <button type="button"
                      onClick={() => setBuka(buka === g.sewing_group_id ? null : g.sewing_group_id)}
                      className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left">
                <span>
                  <span className="font-medium text-foreground">{g.name || g.code}</span>
                  <span className="ml-2 tabular-nums text-sm text-muted-foreground">
                    {g.total_potong} potong · {g.jumlah_fa} FA
                  </span>
                </span>
                <span className="tabular-nums text-lg font-semibold text-foreground">
                  {rupiah(g.total_idr)}
                </span>
              </button>

              {buka === g.sewing_group_id && (
                <div className="mt-3 space-y-1 border-t border-border pt-3">
                  {g.per_jenis.map((j) => (
                    <div key={j.code} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                      <span className="text-foreground">
                        {j.name || j.code}
                        <span className="ml-2 tabular-nums text-muted-foreground">
                          {j.qty} ×{" "}
                          {/* ⛔ Butir 2 — tarif bisa LEBIH DARI SATU di satu
                              jenis, dan itu inti K13/K77: tarif dibekukan per
                              FA. Menampilkan satu angka akan berbohong pada
                              bulan yang memuat FA lama dan FA baru. */}
                          {j.tarif.map(rupiah).join(" / ")}
                        </span>
                      </span>
                      <span className="tabular-nums text-foreground">{rupiah(j.total_idr)}</span>
                    </div>
                  ))}
                  {/* K201 butir 3: selisih koreksi serah terima masuk periode saat koreksi disetujui;
                      periode serah terima aslinya tidak diubah. */}
                  {(g.koreksi_serah_terima || []).map((k) => (
                    <div key={k.handover_lama_id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                      <span className="text-muted-foreground">
                        Koreksi serah terima {k.fa_number || ""} ({tgl(String(k.serah_terima_pada).slice(0, 10))}):{" "}
                        <span className="tabular-nums">{k.qty_lama} → {k.qty_baru} potong</span>
                      </span>
                      <span className="tabular-nums text-foreground">{rupiahTanda(k.selisih_idr)}</span>
                    </div>
                  ))}
                  {(g.koreksi || []).map((k, i) => (
                    <div key={`${k.koreksi_id}-${i}`} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                      <span className="text-muted-foreground">
                        Koreksi tarif ({tgl(String(k.serah_terima_pada).slice(0, 10))}):{" "}
                        <span className="tabular-nums">{k.qty} × {rupiahTanda(k.selisih_per_potong)}</span>
                      </span>
                      <span className="tabular-nums text-foreground">{rupiahTanda(k.selisih_idr)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* ⛔ Butir 1 — selisih yang nol bisa dijelaskan adalah selisih yang
              akan ditanyakan orang. */}
          {data.tanpa_group.total_idr !== 0 && (
            <div className="rounded-xl border border-warning-soft-border bg-warning-soft p-4 text-sm">
              <p className="font-semibold text-warning-soft-foreground">
                {rupiah(data.tanpa_group.total_idr)} tidak punya group
              </p>
              <p className="mt-1 text-warning-soft-foreground">
                Serah terimanya tercatat tanpa menyebut group jahit, jadi upah
                ini tidak bisa ditagihkan ke siapa pun. Tetap dijumlahkan di
                total supaya angkanya jujur.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
